import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Send, Flag, MoreVertical } from 'lucide-react';
import { Comment } from '../../../types/media';

interface PostCommentsProps {
  comments: Comment[];
  onSubmit: (e: React.FormEvent) => void;
  newComment: string;
  onCommentChange: (value: string) => void;
  isSubmitting: boolean;
}

const COMMENTS_PER_PAGE = 5;

export function PostComments({
  comments,
  onSubmit,
  newComment,
  onCommentChange,
  isSubmitting
}: PostCommentsProps) {
  const commentsContainerRef = React.useRef<HTMLDivElement>(null);
  const [commentToReport, setCommentToReport] = React.useState<Comment | null>(null);
  const [showOptionsForComment, setShowOptionsForComment] = React.useState<string | null>(null);
  const [displayedComments, setDisplayedComments] = React.useState<Comment[]>([]);
  const [page, setPage] = React.useState(1);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);

  // Initialize with first page of comments
  React.useEffect(() => {
    setDisplayedComments(comments.slice(0, COMMENTS_PER_PAGE));
  }, [comments]);

  // Handle scroll to load more
  React.useEffect(() => {
    const container = commentsContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (isLoadingMore) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      // Load more when user scrolls to bottom (with 20px threshold)
      if (scrollHeight - scrollTop - clientHeight < 20) {
        loadMoreComments();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [isLoadingMore, page, comments]);

  const loadMoreComments = async () => {
    if (displayedComments.length >= comments.length) return;

    setIsLoadingMore(true);
    // Simulate loading delay
    await new Promise(resolve => setTimeout(resolve, 500));

    const nextPage = page + 1;
    const newComments = comments.slice(0, nextPage * COMMENTS_PER_PAGE);
    setDisplayedComments(newComments);
    setPage(nextPage);
    setIsLoadingMore(false);
  };

  // Update displayed comments when new comment is added
  React.useEffect(() => {
    if (comments.length > displayedComments.length) {
      setDisplayedComments(comments.slice(0, page * COMMENTS_PER_PAGE));
    }
  }, [comments.length]);

  const handleReport = async (comment: Comment) => {
    try {
      setCommentToReport(comment);
    } catch (error) {
      console.error('Error reporting comment:', error);
    }
  };

  return (
    <div className="mt-3 space-y-3">
      <div
        ref={commentsContainerRef}
        className="max-h-48 overflow-y-auto space-y-2.5 custom-scrollbar"
      >
        {displayedComments.map(comment => (
          <div key={comment.id} className="flex items-start gap-2 group">
            <img
              src={comment.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.username)}&background=random&size=24`}
              alt={comment.username}
              className="w-6 h-6 rounded-full flex-shrink-0"
              loading="lazy"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(comment.username)}&background=random&size=24`;
              }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className="font-semibold text-gray-900 dark:text-white text-sm">
                    {comment.username}
                  </span>
                  <span className="ml-1.5 text-gray-600 dark:text-gray-300 text-sm">
                    {comment.content}
                  </span>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                    {comment.createdAt && formatDistanceToNow(
                      comment.createdAt instanceof Date
                        ? comment.createdAt
                        : comment.createdAt.toDate?.() || new Date(comment.createdAt),
                      { addSuffix: true, locale: fr }
                    ).replace('environ ', '')}
                  </p>
                </div>
                <div className="relative flex-shrink-0">
                  <button
                    onClick={() => setShowOptionsForComment(showOptionsForComment === comment.id ? null : comment.id)}
                    className="p-1 text-gray-300 dark:text-gray-600 hover:text-gray-500 dark:hover:text-gray-400 rounded transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <MoreVertical className="w-3.5 h-3.5" />
                  </button>
                  {showOptionsForComment === comment.id && (
                    <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-10">
                      <button
                        onClick={() => {
                          handleReport(comment);
                          setShowOptionsForComment(null);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <Flag className="w-3.5 h-3.5" />
                        <span>Signaler</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {isLoadingMore && (
          <div className="flex justify-center py-2">
            <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-primary-500 rounded-full animate-spin"></div>
          </div>
        )}
      </div>

      <form onSubmit={onSubmit} className="relative">
        <input
          type="text"
          value={newComment}
          onChange={(e) => onCommentChange(e.target.value)}
          placeholder="Ajouter un commentaire..."
          className="w-full pr-10 pl-3 py-2.5 bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 dark:text-white text-sm transition-all"
        />
        <button
          type="submit"
          disabled={!newComment.trim() || isSubmitting}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-primary-500 hover:text-primary-600 dark:text-primary-400 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>

      {commentToReport && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-sm border border-gray-200 dark:border-gray-700">
            <div className="p-5 border-b border-gray-100 dark:border-gray-700">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">
                Signaler ce commentaire
              </h3>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Notre equipe de moderation sera notifiee et examinera ce commentaire.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setCommentToReport(null)}
                  className="px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => {
                    setCommentToReport(null);
                    const toast = document.createElement('div');
                    toast.className = 'fixed bottom-4 left-1/2 -translate-x-1/2 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 px-5 py-2.5 rounded-xl shadow-lg border border-emerald-100 dark:border-emerald-800/30 z-50 flex items-center gap-2 animate-fade-in text-sm font-medium';
                    toast.innerHTML = `
                      <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span>Commentaire signale</span>
                    `;
                    document.body.appendChild(toast);
                    setTimeout(() => toast.remove(), 3000);
                  }}
                  className="px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                >
                  Signaler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}